import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectFortressesUnderConstruction,
  selectHexes,
  selectPendingMovementActions,
  selectPendingShootingActions,
  selectPlayers,
  selectRound,
  selectSpecialBuildings,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {addPendingShootingAction, rerollPendingShootingAction} from "../../../redux/actions";
import {calculateAvailableShootingCells} from "../../gameState/shooting/calculateAvailableShootingCells";
import {checkIfUnitMeleeElf} from "../../factions/elves/checkIfUnitMeleeElf";
import {calculateAvailableShootingCellsForElves} from "../../factions/elves/calculateAvailableShootingCellsForElves";
import {calculateShootingDamage} from "../../gameState/shooting/calculateShootingDamage";
import {isBuildingDestroyed} from "../../gameState/shooting/buildingDamageUtils";
import {hasAdjacentEnemyUnit} from "../../gameState/hasAdjacentEnemyUnit";
import {getPlayerUnits} from "../../players/getPlayerUnits";
import {getUnitById} from "../../unit/getUnitById";
import {findOwner} from "../../gameState/findOwner";
import {checkIfCentralQuarter} from "../../buildings/checkIfCentralQuarter";
import {getDamageDiceRoll} from "../../dice/getDamageDiceRoll";
import {getBotCombatD6MinRoll} from "../../dice/getBotCombatD6MinRoll";
import {getBotD6RollBonus} from "../../dice/getBotD6RollBonus";
import {getLowRatingAdvantageDicePenalty} from "../../dice/getLowRatingAdvantageDicePenalty";
import {getShotOpponentOwnerIds} from "../../shooting/getShotOpponentOwnerIds";
import {BOT_PRESSURE_TARGET_WEIGHT} from "../../../configs/botPressure";
import {getPlayersByIds} from "../../players/getPlayersByIds";
import {createRandomId} from "../../ids/createRandomId";
import {ID_PREFIX_FOR_SHOOTING_ACTION} from "../../../constants/ids";
import {getUnitTypeConfig, UNIT_TYPES} from "../../../configs/units";
import {SPECIAL_BUILDING_TOKENS} from "../../../constants/tokens";
import {D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE} from "../../../configs/war";
import {NEUTRAL_PLAYER_ID} from "../../../constants/players";
import {getOwnQuarterHexKeys} from "../evaluation/cities";
import {keyToCoords} from "../../coords/keyToCoords";
import {getBotPredictions} from "../strongMode/botPredictionsStore";
import {BOT_WEIGHTS} from "../botConfig";

import {checkIfAlreadyBestMeleeCombatDamage} from "../../melee/checkIfAlreadyBestMeleeCombatDamage";
import {checkIfAlreadyBestResultForShootingD4} from "../../shooting/checkIfAlreadyBestResultForShootingD4";
import {selectBotRerollDice} from "./selectBotRerollDice";
import {createBotShotValueGetter} from "./createBotShotValueGetter";

/**
 * Переброс плохих костей стрельбы за дополнительное снабжение (зеркало
 * SelectedUnitShooting.handleRerollClick): живой тест 15.3 — эльфы не
 * перекидывали худшие матожидания. Один переброс за тик.
 */
const tryShootingReroll = (store, state, currentPlayer) => {
  const units = selectUnits(state);
  const players = selectPlayers(state);
  const citiesCache = selectCitiesCache(state);
  const pendingMovementActions = selectPendingMovementActions(state);
  const specialBuildings = selectSpecialBuildings(state);

  for (const action of selectPendingShootingActions(state)) {
    const shooter = getUnitById(units, action.shooterUnitId);
    if (!shooter || shooter.ownerId !== currentPlayer.id) continue;
    if (action.hasRerolled || !shooter.additionalSupply) continue;

    const {diceRoll1, diceRoll2, targetUnitId, targetHexKey} = action;
    const hasMoved = pendingMovementActions.some((moveAction) => moveAction.unitId === shooter.id);
    const isAlreadyBest = checkIfUnitMeleeElf(shooter)
      ? checkIfAlreadyBestMeleeCombatDamage(diceRoll1, diceRoll2)
      : checkIfAlreadyBestResultForShootingD4(hasMoved, diceRoll1, diceRoll2);
    if (isAlreadyBest) continue;
    const targetUnit = targetUnitId ? getUnitById(units, targetUnitId) : null;
    if (!targetUnit && action.damage >= D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE) continue;

    const targetUnitOwnerId = targetUnit ? targetUnit.ownerId : null;
    const targetHexOwnerId = findOwner(targetHexKey, players, citiesCache)?.id ?? null;

    // Переброс собственных костей бота-стрелка: подкрутка d6 его уровнем
    // бонуса (см. getBotD6MinRoll, getBotD6RollBonus; при выстреле по боту ее
    // нет) плюс +1 к полу при стрельбе по игроку под давлением ботов (см.
    // getBotCombatD6MinRoll). От этих же подкруток зависит, какие кости вообще
    // стоит перебрасывать. Цели — те, по кому выстрел действительно бьет (см.
    // getShotOpponentOwnerIds)
    const targetOwners = getPlayersByIds(
      players, getShotOpponentOwnerIds(shooter, targetUnitOwnerId, targetHexOwnerId)
    );
    const shooterMinRoll = getBotCombatD6MinRoll(currentPlayer, targetOwners);
    const shooterRollBonus = getBotD6RollBonus(currentPlayer, targetOwners);
    // Фора новичкам: по цели игрока с форой бот и перебрасывает со штрафом к
    // грани — иначе переброс обходил бы штраф (см.
    // getLowRatingAdvantageDicePenalty)
    const shooterFacePenalty = getLowRatingAdvantageDicePenalty(currentPlayer, targetOwners);

    const {isDice1Selected, isDice2Selected} = selectBotRerollDice({
      diceRoll1,
      diceRoll2,
      minRoll: shooterMinRoll,
      rollBonus: shooterRollBonus,
      facePenalty: shooterFacePenalty,
      getValue: createBotShotValueGetter({
        shooter,
        action,
        units,
        players,
        pendingMovementActions,
        pendingShootingActions: selectPendingShootingActions(state),
        specialBuildings,
        buildingsDamage: selectBuildingsDamage(state),
        targetUnitOwnerId,
        targetHexOwnerId,
      }),
    });
    if (!isDice1Selected && !isDice2Selected) continue;

    const newDiceRoll1 = isDice1Selected
      ? getDamageDiceRoll(shooterMinRoll, shooterRollBonus, shooterFacePenalty)
      : diceRoll1;
    const newDiceRoll2 = isDice2Selected
      ? getDamageDiceRoll(shooterMinRoll, shooterRollBonus, shooterFacePenalty)
      : diceRoll2;
    const {damage: newDamage, bonus: newBonus} = calculateShootingDamage(
      shooter, pendingMovementActions, newDiceRoll1, newDiceRoll2, units, players, targetUnitOwnerId, targetHexOwnerId
    );
    const isUnitTarget = Boolean(targetUnit);
    const isFortress = specialBuildings[targetHexKey] === SPECIAL_BUILDING_TOKENS.FORTRESS;
    const isBadLuck = isUnitTarget || isFortress
      ? newDamage <= action.damage
      : newDamage < D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE;

    store.dispatch(rerollPendingShootingAction(
      action.id, newDiceRoll1, newDiceRoll2, isUnitTarget, isBadLuck, isDice1Selected, isDice2Selected, newDamage, newBonus
    ));
    return true;
  }
  return false;
};

// Гексагональная дистанция по осевым координатам (для проверки близости угрозы)
const calcHexDistance = (cellKeyA, cellKeyB) => {
  const [aq, ar] = keyToCoords(cellKeyA);
  const [bq, br] = keyToCoords(cellKeyB);
  return (Math.abs(aq - bq) + Math.abs(ar - br) + Math.abs((aq + ar) - (bq + br))) / 2;
};

// Ожидаемый штраф кости: перемещавшийся d4 берет худший из двух бросков, стоящий —
// лучший. Грани {-3,-2,-1,-1,0,+1}: E[худшая из 2] = -62/36 ~ -1.7,
// E[лучшая из 2] = -10/36 ~ -0.3
const EXPECTED_DICE_PENALTY_MOVED = 1.7;
const EXPECTED_DICE_PENALTY_STATIONARY = 0.3;

/**
 * Этап стрельбы фазы войны: один выстрел за тик, цель с максимальной ожидаемой ценностью.
 * Только катапульты (d4) — фракционная стрельба эльфов сознательно не используется ботом.
 */
export const decideShootingStep = (store) => {
  const state = store.getState();
  const currentPlayer = selectCurrentPlayer(state);
  const hexes = selectHexes(state);
  const units = selectUnits(state);
  const players = selectPlayers(state);
  const citiesCache = selectCitiesCache(state);
  const specialBuildings = selectSpecialBuildings(state);
  const buildingsDamage = selectBuildingsDamage(state);
  const pendingShootingActions = selectPendingShootingActions(state);
  const pendingMovementActions = selectPendingMovementActions(state);
  const fortressesUnderConstruction = selectFortressesUnderConstruction(state);

  const shooters = getPlayerUnits(currentPlayer, units).filter((unit) => {
    // Катапульты d4 у всех; у эльфов стреляют все отряды (кроме d4 — те по общим правилам)
    if (unit.type !== UNIT_TYPES.D4.id && !checkIfUnitMeleeElf(unit)) {
      return false;
    }
    if (unit.hasShotThisWarPhase) {
      return false;
    }
    if (pendingShootingActions.some((action) => action.shooterUnitId === unit.id)) {
      return false;
    }
    return !hasAdjacentEnemyUnit(unit.hexKey, currentPlayer.id, units);
  });

  if (shooters.length === 0) {
    // Все выстрелы сделаны — перед завершением этапа перебрасываем плохие кости
    // за дополнительное снабжение (живой тест 15.3)
    return tryShootingReroll(store, state, currentPlayer);

  }

  // Ячейки своих отрядов и кварталов — отряды игроков в дистанции <= 3 от них
  // это подошедшая угроза, приоритетная цель (живой тест 14)
  const closeThreatCells = [
    ...getPlayerUnits(currentPlayer, units).map((unit) => unit.hexKey),
    ...getOwnQuarterHexKeys(citiesCache, currentPlayer.color).keys(),
  ];

  // Сильный режим: прогноз позиций противников после их фаз войны
  const predictions = getBotPredictions(currentPlayer.id, selectRound(state));

  // Противники под давлением ботов (см. applyBotPressureLatches): их отряды и
  // строения — более ценная цель. В нерейтинговых партиях набор пуст
  const pressuredEnemyPlayerIds = new Set(
    players.filter((player) => player.isUnderBotFocus && player.id !== currentPlayer.id)
      .map((player) => player.id)
  );

  // A/B: legacy-группы играют по поведению волны 21 (реверт правок волны 22)

  // Волна 19 (отчет №13, эльфы слабейшие; запечено): групповой фокус огня —
  // урон УЖЕ назначенных выстрелов фазы учитывается в эффективном размере цели,
  // чтобы несколько стрелков добивали одну цель (убийство = очки), а не
  // размазывали урон по разным
  const pendingDamageByTarget = new Map();
  pendingShootingActions.forEach((action) => {
    if (action.targetUnitId && action.damage > 0) {
      pendingDamageByTarget.set(
        action.targetUnitId,
        (pendingDamageByTarget.get(action.targetUnitId) || 0) + action.damage
      );
    }
  });

  let bestShot = null;
  let bestScore = 0;

  for (const shooter of shooters) {
    const isMeleeElfShooter = checkIfUnitMeleeElf(shooter);

    const availableShootingCells = isMeleeElfShooter
      ? calculateAvailableShootingCellsForElves(
        hexes,
        units,
        shooter,
        pendingShootingActions,
        specialBuildings,
        buildingsDamage,
        fortressesUnderConstruction,
      )
      : calculateAvailableShootingCells(
        hexes,
        units,
        shooter,
        pendingShootingActions,
        citiesCache,
        currentPlayer,
        specialBuildings,
        buildingsDamage,
        fortressesUnderConstruction,
      );

    const hasMoved = pendingMovementActions.some((action) => action.unitId === shooter.id);
    // Эльфы: урон = половина ближнего боя, штрафа за перемещение нет
    const expectedDamage = isMeleeElfShooter
      ? Math.max(0, shooter.size - EXPECTED_DICE_PENALTY_STATIONARY) / 2
      : shooter.size - (hasMoved ? EXPECTED_DICE_PENALTY_MOVED : EXPECTED_DICE_PENALTY_STATIONARY);

    for (const targetHexKey of availableShootingCells) {
      const targetUnit = units.find((unit) => unit.hexKey === targetHexKey);

      let score = 0;
      if (targetUnit && targetUnit.ownerId !== currentPlayer.id) {
        const targetWinPoints = getUnitTypeConfig(targetUnit.type)?.winPoints || 0;
        // Фокус огня (волна 19): эффективный размер цели за вычетом урона уже
        // назначенных выстрелов; перекрытая цель (добита) — выстрел не тратим
        const pendingDamage = pendingDamageByTarget.get(targetUnit.id) || 0;
        const effectiveTargetSize = targetUnit.size - pendingDamage;
        if (effectiveTargetSize <= 0) {
          continue;
        }
        if (expectedDamage >= effectiveTargetSize) {
          score = 50 + targetWinPoints * 8;
        } else {
          score = Math.max(0.5, expectedDamage * 3);
          // Цель уже под огнем — концентрируем следующие выстрелы на ней.
          // Волна 22 (21.7): у эльфов (несколько стрелков за фазу) бонус
          // концентрации залпа на одной цели удвоен — весь урон в одну цель
          if (pendingDamage > 0) {
            score += isMeleeElfShooter ? 30 : 15;
          }
        }
        if (targetUnit.ownerId === NEUTRAL_PLAYER_ID) {
          // Нейтралы неподвижны и не угрожают — отряды игроков важнее.
          // Волна 22 (21.7): эльфы предпочитают цели игроков еще жестче
          score *= isMeleeElfShooter ? 0.15 : 0.3;
        } else {
          if (closeThreatCells.some((cellKey) => calcHexDistance(cellKey, targetHexKey) <= 3)) {
            // Отряд игрока возле своих отрядов/городов — подошедшая угроза
            score += 25;
          }
          // Цель смежна другому своему отряду — расстрелять перед ближним боем
          // (живой тест 15.4: ослабить противника до размена)
          if (units.some((ownUnit) =>
            ownUnit.ownerId === currentPlayer.id
            && ownUnit.id !== shooter.id
            && calcHexDistance(ownUnit.hexKey, targetHexKey) <= 1)
          ) {
            score += 20;
          }
          // Сильный режим: цель, которая по прогнозу окажется вплотную к нашим
          // отрядам/городам после своей фазы войны, — бьем на подходе
          const predictedCell = predictions?.predictedPositionByUnitId?.get(targetUnit.id);
          if (predictedCell
            && closeThreatCells.some((cellKey) => calcHexDistance(cellKey, predictedCell) <= 2)
          ) {
            score += BOT_WEIGHTS.STRONG_PREDICTED_APPROACH_SHOT_BONUS;
          }
        }
      } else if (!targetUnit && !isMeleeElfShooter) {
        // Стрельба по строению — только катапульты d4 (по правилам другие типы
        // урон строениям не наносят) и только при уроне >= 4 (жетон повреждения)
        score = expectedDamage >= D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE ? 3 : 0;
      }

      // Давление ботов: выстрел по игроку, против которого боты играют
      // сообща, ценится выше (вес, а не приказ — см. src/configs/botPressure.js)
      if (score > 0) {
        const targetOwnerId = targetUnit
          ? targetUnit.ownerId
          : findOwner(targetHexKey, players, citiesCache)?.id;
        if (pressuredEnemyPlayerIds.has(targetOwnerId)) {
          score *= BOT_PRESSURE_TARGET_WEIGHT;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestShot = {shooter, targetHexKey, targetUnit};
      }
    }
  }

  if (!bestShot) {
    return false;
  }

  const {shooter, targetHexKey, targetUnit} = bestShot;

  // Дальше — точное повторение диспатча GameScene.handleShootingPhaseClick
  let isCentralQuarterDamageIgnored = false;
  if (checkIfCentralQuarter(hexes[targetHexKey])) {
    let cityWithCentralQuarter = null;
    for (const cities of Object.values(citiesCache)) {
      cityWithCentralQuarter = cities.find((city) => city.centralQuarter === targetHexKey);
      if (cityWithCentralQuarter) {
        break;
      }
    }

    if (cityWithCentralQuarter?.quarter && cityWithCentralQuarter.quarter.length > 0) {
      isCentralQuarterDamageIgnored = cityWithCentralQuarter.quarter.some((quarterHexKey) =>
        !isBuildingDestroyed(quarterHexKey, specialBuildings, buildingsDamage, pendingShootingActions, hexes)
      );
    }
  }

  const id = createRandomId(ID_PREFIX_FOR_SHOOTING_ACTION);

  const isUnitTarget = Boolean(targetUnit);
  const targetUnitId = isUnitTarget ? targetUnit.id : null;
  const targetUnitOwnerId = isUnitTarget ? getUnitById(units, targetUnitId).ownerId : null;
  const targetHexOwnerId = findOwner(targetHexKey, players, citiesCache)?.id ?? null;

  // Кости стрельбы бота: подкрутка d6 его уровнем бонуса (см.
  // getBotD6MinRoll, getBotD6RollBonus; при выстреле по боту ее нет) плюс +1 к
  // полу при стрельбе по игроку под давлением ботов. Цели — те, по кому
  // выстрел действительно бьет: у эльфов это только отряд (см.
  // getShotOpponentOwnerIds)
  const shotTargetOwners = getPlayersByIds(
    players, getShotOpponentOwnerIds(shooter, targetUnitOwnerId, targetHexOwnerId)
  );
  const shooterD6MinRoll = getBotCombatD6MinRoll(currentPlayer, shotTargetOwners);
  const shooterD6RollBonus = getBotD6RollBonus(currentPlayer, shotTargetOwners);
  // Фора новичкам: по цели игрока с форой — штраф к грани (см.
  // getLowRatingAdvantageDicePenalty)
  const shooterFacePenalty = getLowRatingAdvantageDicePenalty(currentPlayer, shotTargetOwners);
  const diceRoll1 = getDamageDiceRoll(shooterD6MinRoll, shooterD6RollBonus, shooterFacePenalty);
  const diceRoll2 = getDamageDiceRoll(shooterD6MinRoll, shooterD6RollBonus, shooterFacePenalty);

  const {damage, bonus} = calculateShootingDamage(
    shooter, pendingMovementActions, diceRoll1, diceRoll2, units, players, targetUnitOwnerId, targetHexOwnerId
  );

  const isFortressTarget = specialBuildings[targetHexKey] === SPECIAL_BUILDING_TOKENS.FORTRESS;
  const isBadLuck = isUnitTarget || isFortressTarget
    ? damage <= 0
    : damage < D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE;

  store.dispatch(addPendingShootingAction(
    id,
    shooter.id,
    targetUnitId,
    targetHexKey,
    diceRoll1,
    diceRoll2,
    isBadLuck,
    isCentralQuarterDamageIgnored,
    damage,
    bonus,
    checkIfUnitMeleeElf(shooter), // isUnitDamageOnly для стреляющих эльфов
  ));

  return true;
};
